import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationService, Type } from '@progress/kendo-angular-notification';
import { FileInfo, SelectEvent } from '@progress/kendo-angular-upload';
import { Observable, catchError, retry } from 'rxjs';
import { AppConfigService } from 'src/app/services/app-config.service';
import { LoadingService } from 'src/app/services/loading.service';
import { handleError } from 'src/app/shared/functions/error-handler';
import { assertTrustedUrl } from 'src/app/shared/functions/validate-url';
import { IAppConfig } from 'src/app/shared/interfaces/app-config';
import { FilterParam } from '../../dashboard/interface/agency-filter';
import { AgencyList, VwAgency } from '../../dashboard/interface/agency-list';
import { WaitlistRequest } from '../../dashboard/models/waitlist-request';
import { AgencyRequest, workListRequest } from '../interfaces/add-to-worklist';
import {
  AddWorkHoursRequest,
  AddWorkHoursResponse,
  AgencyLocationInfo,
  AgencyWorkHoursResponse,
  AssessmentContractResponse,
  ProviderInformationResponse,
  ProviderScheduleResponse,
  RelatedServiceContractResponse,
  SchoolDetailsResponse,
  SchoolYearsAndTermsResponse,
  UploadDocumentResponse
} from '../interfaces/admin-service-types';
import { VwCompleteWorklist } from '../interfaces/complete-worklist';
import { EvaluatorProvider } from '../interfaces/evaluator-providers';
import { VwMdeWaitlist } from '../interfaces/mde-waitlist';
import { SearchGlobalWorklistRequest } from '../interfaces/search-global-worklist';
import { ISubmitUdoPayload } from '../interfaces/submit-udo-payload';
import { SaveAssessmentContractModel } from '../models/save-assesment-contract-model';
import { SaveRelatedServiceContractModel } from '../models/save-related-service-contract-model';
import { ISchedulerResponse } from '../../provider-management/interfaces/get-scheduler-response';

const NOTIFICATION_HIDE_DELAY = 5000;
const ANIMATION_DURATION = 200;
const RETRY_COUNT = 1;

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  config: IAppConfig;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private notificationService: NotificationService,
    private loadingService: LoadingService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  private trustedApiUrl(path: string): string {
    return assertTrustedUrl(`${this.config.apiUrl}${path}`, this.config.apiUrl);
  }

  private trustedStudentApiUrl(path: string): string {
    return assertTrustedUrl(
      `${this.config.studentApiUrl}${path}`,
      this.config.studentApiUrl
    );
  }

  private sanitizePathSegment(value: string): string {
    return encodeURIComponent(String(value ?? '').replace(/[^A-Za-z0-9_-]/g, ''));
  }

  private assertSafeIdentifier(value: string): string {
    const candidate: string = String(value ?? '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(candidate)) {
      throw new Error('Invalid identifier');
    }
    return candidate;
  }

  public getGobalWorklist(payload: workListRequest): Observable<VwCompleteWorklist[]> {
    const url = this.trustedApiUrl('/MDE/GlobalWorklist');
    return this.http
      .post<VwCompleteWorklist[]>(url, payload)
      .pipe(catchError(handleError));
  }


  public getMdeWaitlist(agencySk: string, payload: workListRequest): Observable<VwMdeWaitlist[]> {
    const url = this.trustedApiUrl(
      `/MDE/Waitlist/${encodeURIComponent(agencySk)}`
    );
    return this.http
      .post<VwMdeWaitlist[]>(url, payload)
      .pipe(catchError(handleError));
  }

  public getMdeAgency(): Observable<AgencyList[]> {
    const url = this.trustedApiUrl('/MDE/MDEAgencies');
    return this.http
      .get<AgencyList[]>(url)
      .pipe(catchError(handleError));
  }

  public getMDEAgencyList(filterParam: FilterParam): Observable<VwAgency[]> {
    const url = this.trustedApiUrl('/MDE/GetMDEAgencyList');
    return this.http
      .post<VwAgency[]>(url, filterParam)
      .pipe(catchError(handleError));
  }

  public saveMdeAgency(request: AgencyRequest): Observable<boolean> {
    const url = this.trustedApiUrl('/MDE/AddAgencyDetailsPerLocation');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public getAgencyDetails(studentSk: string): Observable<VwAgency[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/GetMDEListByStudentSk/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<VwAgency[]>(url)
      .pipe(catchError(handleError));
  }

  public GetMDEDetailsByStudentSk(studentSk: string): Observable<VwAgency[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/GetMDEDetailsByStudentSk/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<VwAgency[]>(url)
      .pipe(catchError(handleError));
  }

  public getMdeAgencyByParentAgencySk(agencySk: string): Observable<AgencyList> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/MDEParentAgency/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyList>(url)
      .pipe(catchError(handleError));
  }

  public getMdeAgencyByAgencySk(agencySk: string): Observable<AgencyList> {
    const safeAgencySk: string = this.assertSafeIdentifier(agencySk);
    const url = this.trustedApiUrl(`/MDE/MDEAgency/${safeAgencySk}`);
    return this.http
      .get<AgencyList>(url)
      .pipe(catchError(handleError));
  }

  public addToWorkList(request: AgencyRequest[]): Observable<boolean> {
    const url = this.trustedApiUrl('/MDE/AddToWorklist');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public addToWaitlist(request: AgencyRequest): Observable<boolean> {
    const url = this.trustedApiUrl('/MDE/AddToWaitlist');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public removeFromWaitlist(request: AgencyRequest[]): Observable<boolean> {
    const url = this.trustedApiUrl('/MDE/RemoveFromWaitlist');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public removeFromWorklist(studentSk: string): Observable<boolean> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/RemoveFromWorklist/${encodeURIComponent(studentSk)}`,
      this.config.apiUrl
    );
    return this.http
      .post<boolean>(url, null)
      .pipe(catchError(handleError));
  }

  public addToMDWaitlist(request: WaitlistRequest): Observable<boolean> {
    const url = this.trustedApiUrl('/MDE/AddToMDEWaitlist');
    return this.http
      .post<boolean>(url, request)
      .pipe(catchError(handleError));
  }

  public getStudentSearchDetails(
    request: SearchGlobalWorklistRequest
  ): Observable<VwCompleteWorklist[]> {
    const url = this.trustedApiUrl('/MDE/Worklist');
    return this.http
      .post<VwCompleteWorklist[]>(url, request)
      .pipe(catchError(handleError));
  }

  public getEvaluatorProvidersByAgency(agencySk: string): Observable<EvaluatorProvider[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/AgencyManagement/EvaluatorProvidersByAgency/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<EvaluatorProvider[]>(url)
      .pipe(catchError(handleError));
  }
  public getAssessmentsContractList(agencySk: string): Observable<AssessmentContractResponse> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/AgencyManagement/AgencyAssessmentContract/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AssessmentContractResponse>(url)
      .pipe(retry(1), catchError(handleError));
  }

 public getAgencyRelatedServiceContract(
  agencySk: string,
  startDate?: Date | null,
  endDate?: Date | null
): Observable<RelatedServiceContractResponse> {
  let url =
    `${this.config.apiUrl}/AgencyManagement/AgencyRelatedServiceContract/${encodeURIComponent(agencySk)}`;
  const queryParams: string[] = [];
  if (startDate) {
    queryParams.push(
      `startDate=${encodeURIComponent(
        startDate.toISOString().split('T')[0]
      )}`
    );
  }
  if (endDate) {
    queryParams.push(
      `endDate=${encodeURIComponent(
        endDate.toISOString().split('T')[0]
      )}`
    );
  }
  if (queryParams.length > 0) {
    url += `?${queryParams.join('&')}`;
  }
  const trustedUrl = assertTrustedUrl(
    url,
    this.config.apiUrl
  );
  return this.http
    .get<RelatedServiceContractResponse>(trustedUrl)
    .pipe(
      retry(1),
      catchError(handleError)
    );
}

  public getAgencyWorkHours(
    providerSk: string,
    agencySk?: string,
    schoolYearId?: string,
    termId?: string
  ): Observable<AgencyWorkHoursResponse[]> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('providerSk', providerSk);
    if (agencySk) params.append('agencySk', agencySk);
    if (schoolYearId) params.append('schoolYearId', schoolYearId);
    if (termId) params.append('termId', termId);
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Provider/GetProviderWorkDetails?${params.toString()}`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyWorkHoursResponse[]>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getSchoolDetails(schoolId: string): Observable<SchoolDetailsResponse> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('schoolId', schoolId);
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Provider/GetSchoolDetails?${params.toString()}`,
      this.config.apiUrl
    );
    return this.http
      .get<SchoolDetailsResponse>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getSchoolYearsAndTerms(): Observable<SchoolYearsAndTermsResponse> {
    const url = this.trustedApiUrl('/Provider/GetSchoolYearsAndTerms');
    return this.http
      .get<SchoolYearsAndTermsResponse>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getProviderInformation(
    providerSk: string,
    agencySK?: string
  ): Observable<ProviderInformationResponse> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('providerSk', providerSk);
    if (agencySK) params.append('agencySk', agencySK);
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Provider/GetProviderAndAgencyInfo?${params.toString()}`,
      this.config.apiUrl
    );
    return this.http
      .get<ProviderInformationResponse>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public addAgencyWorkHours(request: AddWorkHoursRequest[]): Observable<AddWorkHoursResponse> {
    const url = this.trustedApiUrl('/Provider');
    return this.http
      .post<AddWorkHoursResponse>(url, request)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public addAgency(data: SaveAssessmentContractModel): Observable<boolean> {
    const url = this.trustedApiUrl('/AgencyManagement/AddAgencyContract');
    return this.http
      .post<boolean>(url, data)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public addRelatedService(data: SaveRelatedServiceContractModel): Observable<boolean> {
    const url = this.trustedApiUrl('/AgencyManagement/AddRelatedService');
    return this.http
      .post<boolean>(url, data)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public inactiveAssessments(data: string[]): Observable<boolean> {
    const url = this.trustedApiUrl('/AgencyManagement/ActiveAgency');
    return this.http
      .put<boolean>(url, data)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public inactiveRelatedServices(date: string, data: string[]): Observable<boolean> {
    const safeDate = encodeURIComponent(date);
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/AgencyManagement/ActiveRelatedAgency?Effectivedate=${safeDate}`,
      this.config.apiUrl
    );
    return this.http
      .put<boolean>(
        url,
        data
      )
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }
  public getMDEAgencyListByAgencySk(agencySk: string): Observable<AgencyList[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/GetMDEAgencyList/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyList[]>(url)
      .pipe(catchError(handleError));
  }
  public getAllAgencyLocations(agencySk: string): Observable<AgencyLocationInfo[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/AllAgencyLocations/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyLocationInfo[]>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }
  public getMDEAgencyPerLocation(AgencyLocationSK: string): Observable<AgencyList> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/MDE/MDEAgencyPerLocation/${encodeURIComponent(AgencyLocationSK)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AgencyList>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public getProviderSchedule(providerSk: string, startDate: string, endDate: string): Observable<ProviderScheduleResponse> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('providerSk', providerSk);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Provider/GetProviderSchedules?${params.toString()}`,
      this.config.apiUrl
    );
    return this.http
      .get<ProviderScheduleResponse>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public  getStudentClanderDetails(studentSk: string, startDate: string, endDate: string): Observable<ISchedulerResponse[]> {
    const params: URLSearchParams = new URLSearchParams();
    params.append('studentSk', studentSk);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    const url = this.trustedStudentApiUrl(
      `/Student/GetStudentCalendar?${params.toString()}`
    );
    return this.http
      .get<ISchedulerResponse[]>(url)
      .pipe(retry(RETRY_COUNT), catchError(handleError));
  }

  public uploadDocument(formData: FormData): Observable<UploadDocumentResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      enctype: 'multipart/form-data',
      Accept: 'application/json',
    });
    const url = this.trustedApiUrl('/AgencyManagement/UploadAwardContract');
    return this.http
      .post<UploadDocumentResponse>(
        url,
        formData,
        { headers }
      )
      .pipe(catchError(handleError));
  }

  public submitAgencyProvider(request: ISubmitUdoPayload): Observable<string> {
    const url = this.trustedApiUrl('/Provider/SubmitAgencyProviderUDO');
    return this.http
      .post<string>(url, request)
      .pipe(retry(1), catchError(handleError));
  }

  public getUdoAgencyInfo(providerSk: string, agencySk: string): Observable<ISubmitUdoPayload> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Provider/GetUdoAgencyProviderInfo/${encodeURIComponent(providerSk)}/${encodeURIComponent(agencySk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ISubmitUdoPayload>(url)
      .pipe(retry(1), catchError(handleError));
  }

  validateAndUploadFile(ev: SelectEvent): void {
    const file: FileInfo = ev.files[0];
    if (file) {
      const allowedExtensions: string[] = ['.xls', '.xlsx'];
      const fileExtension: string = file.name
        .slice(file.name.lastIndexOf('.'))
        .toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        this.showMessage(
          'Allowed file types: .xls, .xlsx', 'error'
        );
        return;
      } else {

        const formData: FormData = new FormData();
        formData.append('file', file.rawFile);
        formData.append('fileType', '2');
        this.loadingService.show();
        this.uploadDocument(formData).subscribe((resp: UploadDocumentResponse) => {
          this.loadingService.hide();
          if (resp?.status === false) {
            this.showMessage('File upload failed', 'error');
            return;
          }
          this.showMessage('File uploaded successfully', 'success');
        });
      }
    }
  }
  showMessage(message: string, type: Type['style']): void {
    if (message) {
      this.notificationService.show({
        content: message,
        hideAfter: NOTIFICATION_HIDE_DELAY,
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'slide', duration: ANIMATION_DURATION },
        type: { style: type, icon: true },
      });
    }
  }
}
